import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ChevronRight, AlertCircle, Moon, Layers, Star } from "lucide-react";
import { Card, SectionLabel } from "@/components/ui/Card";
import { SPORT_LABELS, fmtDuration } from "@/lib/tss";
import { cn } from "@/lib/utils";
import type { WeekWorkoutEntry } from "@/types";

interface Props {
  weekNumber: number;
  phase: string;
  startDate: string;
  endDate: string;
  targetTss: number;
  actualTss: number;
  completionPct: number;
  workouts: WeekWorkoutEntry[];
}

export function CurrentWeekPreview({
  weekNumber,
  phase,
  targetTss,
  actualTss,
  completionPct,
  workouts,
}: Props) {
  const { t } = useTranslation();
  const completedCount = workouts.filter((w) => w.status === "completed").length;
  const hasSunday = workouts.some((w) => {
    const d = new Date(w.date);
    return d.getDay() === 0;
  });

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <SectionLabel>{t("dashboard.week_phase", { n: weekNumber, phase })}</SectionLabel>
          <h2 className="mt-1 font-serif text-[20px] font-medium">{t("dashboard.week_plan")}</h2>
        </div>
        <Link
          to={`/plan/week/${weekNumber}`}
          className="text-[12px] font-medium text-amber-dark hover:underline"
        >
          {t("dashboard.all_weeks")}
        </Link>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[12px]">
        <div className="rounded-md bg-cream px-2 py-2">
          <div className="text-[10px] uppercase tracking-wider text-taupe">{t("dashboard.target")}</div>
          <div className="metric-number mt-0.5 text-[16px]">{targetTss} TSS</div>
        </div>
        <div className="rounded-md bg-cream px-2 py-2">
          <div className="text-[10px] uppercase tracking-wider text-taupe">{t("dashboard.done")}</div>
          <div className="metric-number mt-0.5 text-[16px]">
            {Math.round(actualTss)}{" "}
            <span className="text-[11px] text-taupe">
              ({Math.round(completionPct)}%)
            </span>
          </div>
        </div>
        <div className="rounded-md bg-cream px-2 py-2">
          <div className="text-[10px] uppercase tracking-wider text-taupe">{t("dashboard.workouts")}</div>
          <div className="metric-number mt-0.5 text-[16px]">
            {completedCount} / {workouts.length}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        {workouts.map((w) => (
          <WorkoutDayRow key={w.id} workout={w} />
        ))}
        {!hasSunday ? <RestDayRow /> : null}
      </div>
    </Card>
  );
}

function WorkoutDayRow({ workout }: { workout: WeekWorkoutEntry }) {
  const { t } = useTranslation();
  const { is_today, is_past, status } = workout;
  return (
    <Link
      to={`/workout/planned/${workout.id}`}
      className={cn(
        "group flex items-center gap-3 rounded-lg border p-2.5 pl-3 transition-colors",
        is_today
          ? "border-amber/50 bg-amber/5"
          : "border-cream-border bg-cream-light hover:border-amber/40",
        is_past && status !== "completed" ? "opacity-70" : "",
      )}
    >
      <div
        className={cn(
          "flex shrink-0 flex-col items-center justify-center w-10 text-center",
          is_today ? "text-amber-dark" : "text-navy-deep",
        )}
      >
        <span className="text-[9px] uppercase tracking-wider text-taupe">
          {workout.day_of_week_short}
        </span>
        <span className="metric-number text-[16px] font-medium">
          {workout.day_of_month}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-taupe">
            {SPORT_LABELS[workout.sport] ?? workout.sport} · {workout.type}
          </span>
          {workout.is_key_session ? (
            <span className="inline-flex items-center gap-1 rounded bg-amber/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-dark">
              <Star className="h-2.5 w-2.5" strokeWidth={2.5} />
              key
            </span>
          ) : null}
          {workout.is_compound ? (
            <span className="inline-flex items-center gap-1 rounded bg-navy/10 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-navy-deep">
              <Layers className="h-2.5 w-2.5" strokeWidth={2} />
              compound
            </span>
          ) : null}
          {status === "completed" ? (
            <CheckCircle2 className="h-3 w-3 text-green-700" strokeWidth={2} />
          ) : status === "missed" ? (
            <AlertCircle className="h-3 w-3 text-coral-dark" strokeWidth={2} />
          ) : null}
        </div>
        <div className="truncate text-[13px] font-medium text-navy-deep">
          {workout.title}
        </div>
        <div className="text-[11px] text-taupe">
          {fmtDuration(workout.duration_min)} · TSS {workout.target_tss}
          {workout.actual_tss != null
            ? ` · ${t("dashboard.actual_short", { n: Math.round(workout.actual_tss) })}`
            : ""}
        </div>
      </div>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-taupe group-hover:text-amber-dark"
        strokeWidth={1.8}
      />
    </Link>
  );
}

function RestDayRow() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 rounded-lg border border-dashed border-cream-border bg-cream-light/60 p-2.5 pl-3">
      <div className="flex w-10 shrink-0 flex-col items-center">
        <span className="text-[9px] uppercase tracking-wider text-taupe">{t("weekday_sun_short", { defaultValue: "sun" })}</span>
        <Moon className="mt-0.5 h-4 w-4 text-taupe" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium text-navy-deep">
          {t("dashboard.rest_day_full")}
        </div>
        <div className="text-[11px] text-taupe">
          {t("dashboard.morning_only_mcgill")}
        </div>
      </div>
    </div>
  );
}
